import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'mc-theme';

/**
 * useTheme — manages light/dark mode for mc-frontend.
 *
 * Strategy:
 *  1. Check localStorage for a saved preference.
 *  2. Fall back to light mode (default).
 *  3. Apply by toggling the `dark` class on <html>.
 */
const useTheme = () => {
  const getInitialTheme = () => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
    return 'light';
  };

  const [theme, setThemeState] = useState(getInitialTheme);

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    if (t === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  const setTheme = useCallback(
    (t) => {
      localStorage.setItem(STORAGE_KEY, t);
      setThemeState(t);
      applyTheme(t);
    },
    [applyTheme]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const isDark = theme === 'dark';

  return { theme, isDark, setTheme, toggleTheme };
};

export default useTheme;
